"use strict";
/**
 * Authentication Module
 * Phone-OTP login (request code → verify code), session management, and
 * Kalshi API credentials storage. Secret-key login remains as the
 * break-glass path (?legacy=1) while AUTH_LEGACY_SECRET_LOGIN is on
 * server-side — see PHONE_OTP_AUTH_PLAN.md.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDeviceId = getDeviceId;
exports.requestOtpCode = requestOtpCode;
exports.authenticateWithOtp = authenticateWithOtp;
exports.authenticateWithSecretKey = authenticateWithSecretKey;
exports.storeSessionToken = storeSessionToken;
exports.getSessionToken = getSessionToken;
exports.clearSessionToken = clearSessionToken;
exports.parsePermissions = parsePermissions;
exports.storePermissions = storePermissions;
exports.getStoredPermissions = getStoredPermissions;
exports.isSessionValid = isSessionValid;
exports.restoreSession = restoreSession;
exports.logout = logout;
exports.isRateLimited = isRateLimited;
exports.recordFailedAttempt = recordFailedAttempt;
exports.storeApiCredentials = storeApiCredentials;
exports.hasApiCredentials = hasApiCredentials;
exports.clearApiCredentialsStored = clearApiCredentialsStored;
exports.validateApiCredentials = validateApiCredentials;
const state_1 = require("./state");
// Imported from the leaf module, not "./api" — api/request.ts imports this
// file, so going through the barrel would deepen that cycle. network-error.ts
// has no imports of its own.
const network_error_1 = require("./api/network-error");
const proxy_url_1 = require("./api/proxy-url");
const api_1 = require("./api");
// Environment variables - using a simple approach
// In production, this should be set via webpack.DefinePlugin
// For now, we'll use a hardcoded default that can be changed
const SESSION_KEY = "kalshi_session_token";
const SESSION_EXPIRY_KEY = "kalshi_session_expiry";
const SECRET_KEY_KEY = "kalshi_secret_key";
// Cached alongside the token so gating renders instantly (flash-free) on
// reload; reconciled against GET /auth/session in restoreSession(). Not a
// security boundary — the server re-derives permissions on every request.
const PERMISSIONS_KEY = "kalshi_permissions";
// Stable per-browser id so the server can flag logins from a device it
// hasn't seen before (operator login alerts — see routes/auth.ts). NOT a
// security credential: it's advisory metadata only, hence localStorage
// (persists across sessions) and no secrecy requirement.
const DEVICE_ID_KEY = "kalshi_device_id";
/**
 * Read (or lazily create + persist) this browser's device id. Returns a
 * fresh, non-persisted id if localStorage is unavailable, so login still
 * works — the login just reads as a "new device" server-side.
 */
function getDeviceId() {
    try {
        let id = localStorage.getItem(DEVICE_ID_KEY);
        if (!id) {
            id =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(DEVICE_ID_KEY, id);
        }
        return id;
    }
    catch {
        return "unknown";
    }
}
const getProxyUrl = () => (0, proxy_url_1.proxyUrlFor)(typeof window !== "undefined" ? window.location : undefined);
const API_PROXY_URL = getProxyUrl();
// ============================================================================
// Performance Optimization: Cache session token in memory
// Avoids session storage I/O on every request (saves 1-3ms per request)
// SECURITY: Stores session token (NOT secret key) - expires in 30 minutes
// ============================================================================
let cachedSessionToken = null;
let cachedSessionExpiry = null;
/**
 * Ask the server to send an OTP SMS to `phone`.
 * The server replies { sent: true } for every well-formed number
 * (registered or not) — only malformed numbers and rate limits error.
 */
async function requestOtpCode(phone) {
    let response;
    try {
        response = await fetch(API_PROXY_URL + "/auth/request-code", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ phone }),
        });
    }
    catch (error) {
        // Never reached the server. Report that specifically rather than
        // "could not send code", which reads as a Twilio/number problem.
        console.error("Code request error (network):", error);
        return {
            sent: false,
            error: (0, network_error_1.networkErrorFrom)(error, "/auth/request-code").message,
        };
    }
    try {
        const data = await response.json();
        if (data.sent) {
            return { sent: true };
        }
        return { sent: false, error: data.error || "Could not send code" };
    }
    catch (error) {
        console.error("Code request error:", error);
        return { sent: false, error: "Could not send code" };
    }
}
/**
 * Log in with a phone number + OTP code.
 * Returns true if successful, false otherwise.
 */
async function authenticateWithOtp(phone, code) {
    // deviceId lets the server flag logins from an unrecognized browser in
    // the operator SMS alert; it's advisory metadata, never a credential.
    return login({ phone, code, deviceId: getDeviceId() });
}
/**
 * Log in with the legacy shared secret key (break-glass path).
 * SECURITY: Secret key is sent to server once, session token returned
 */
async function authenticateWithSecretKey(secret_key) {
    // deviceId lets the server flag a break-glass login from an unrecognized
    // browser in the operator SMS alert, same as the OTP path.
    return login({ secretKey: secret_key, deviceId: getDeviceId() });
}
/**
 * Returns false when the server rejected the credential. THROWS NetworkError
 * when the request never reached the server.
 *
 * The distinction is load-bearing: this used to return false for both, so a
 * blocked network rendered as "Invalid or expired code" and the operator would
 * burn OTP attempts against a rate limit chasing a network problem.
 */
async function login(body) {
    let response;
    try {
        response = await fetch(API_PROXY_URL + "/auth/login", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });
    }
    catch (error) {
        throw (0, network_error_1.networkErrorFrom)(error, "/auth/login");
    }
    try {
        const data = await response.json();
        if (data.authenticated && data.sessionToken) {
            // Store session token (NOT the credential used to obtain it)
            storeSessionToken(data.sessionToken, data.expiresAt);
            // Capture the permission set the server issued with this session
            // (used for cosmetic UI gating — see permissions.ts).
            const permissions = parsePermissions(data.permissions);
            storePermissions(permissions);
            // Update app state
            (0, state_1.updateState)({
                isAuthenticated: true,
                sessionToken: data.sessionToken,
                permissions,
                currentPage: "events",
                errorMessage: null,
            });
            console.log(`[Auth] Session created, expires at ${new Date(data.expiresAt).toLocaleTimeString()}`);
            return true;
        }
        return false;
    }
    catch (error) {
        // Reached only for a malformed/non-JSON body from a server that DID
        // answer (e.g. a captive portal's HTML). Still a rejected login, not a
        // network failure — the NetworkError path is above.
        console.error("Authentication error:", error);
        return false;
    }
}
/**
 * Store session token in session storage
 * OPTIMIZED: Also cache in memory to avoid I/O on every request
 * SECURITY: Stores session token (NOT secret key) - expires automatically
 */
function storeSessionToken(sessionToken, expiresAt) {
    // Update cache first (fast)
    cachedSessionToken = sessionToken;
    cachedSessionExpiry = expiresAt;
    // Then persist to session storage
    try {
        sessionStorage.setItem(SESSION_KEY, sessionToken);
        sessionStorage.setItem(SESSION_EXPIRY_KEY, expiresAt.toString());
    }
    catch (error) {
        console.error("Failed to store session token:", error);
    }
}
/**
 * Get session token from cache or session storage
 * OPTIMIZED: Return cached value if available (avoids I/O)
 * SECURITY: Returns session token (NOT secret key)
 */
function getSessionToken() {
    // Check cache first (fast path)
    if (cachedSessionToken !== null && cachedSessionExpiry !== null) {
        // Check if expired
        if (Date.now() < cachedSessionExpiry) {
            return cachedSessionToken;
        }
        // Expired - clear cache
        cachedSessionToken = null;
        cachedSessionExpiry = null;
    }
    // Load from session storage (slow path)
    try {
        const token = sessionStorage.getItem(SESSION_KEY);
        const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
        if (token && expiry) {
            const expiryTime = parseInt(expiry, 10);
            // Check if expired
            if (Date.now() < expiryTime) {
                // Update cache
                cachedSessionToken = token;
                cachedSessionExpiry = expiryTime;
                return token;
            }
            // Expired - clear storage
            sessionStorage.removeItem(SESSION_KEY);
            sessionStorage.removeItem(SESSION_EXPIRY_KEY);
        }
        return null;
    }
    catch (error) {
        console.error("Failed to retrieve session token:", error);
        return null;
    }
}
/**
 * Clear session token from cache and storage
 */
function clearSessionToken() {
    // Clear cache
    cachedSessionToken = null;
    cachedSessionExpiry = null;
    // Clear session storage
    try {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_EXPIRY_KEY);
    }
    catch (error) {
        console.error("Failed to clear session token:", error);
    }
}
// ============================================================================
// Permissions (cosmetic UI gating — the server is the real boundary)
// ============================================================================
/**
 * Defensively coerce an unknown value (server response or parsed storage)
 * into a `Permissions` object. Returns `null` if the shape is unusable, so
 * callers fall back to the fail-open default rather than trusting garbage.
 */
function parsePermissions(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const p = raw;
    if (typeof p.level !== "number")
        return null;
    if (p.maxBetUsd !== null && typeof p.maxBetUsd !== "number")
        return null;
    if (!Array.isArray(p.features))
        return null;
    return {
        level: p.level,
        maxBetUsd: p.maxBetUsd ?? null,
        allowManualPrice: p.allowManualPrice === true,
        allowRawBatchOrders: p.allowRawBatchOrders === true,
        allowSellOrders: p.allowSellOrders === true,
        allowCloseOpposite: p.allowCloseOpposite === true,
        allowAdditionalMarkets: p.allowAdditionalMarkets === true,
        allowParallelMarkets: p.allowParallelMarkets === true,
        features: p.features.filter((f) => typeof f === "string"),
    };
}
/** Persist (or clear, when `null`) the permission set to sessionStorage. */
function storePermissions(permissions) {
    try {
        if (permissions) {
            sessionStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
        }
        else {
            sessionStorage.removeItem(PERMISSIONS_KEY);
        }
    }
    catch (error) {
        console.error("Failed to store permissions:", error);
    }
}
/** Read the cached permission set from sessionStorage (or `null`). */
function getStoredPermissions() {
    try {
        const stored = sessionStorage.getItem(PERMISSIONS_KEY);
        return stored ? parsePermissions(JSON.parse(stored)) : null;
    }
    catch (error) {
        console.error("Failed to read stored permissions:", error);
        return null;
    }
}
/**
 * Check if session is still valid
 */
function isSessionValid() {
    const token = getSessionToken();
    return token !== null;
}
/**
 * Restore session from storage.
 *
 * Permissions are hydrated in two steps so gating renders without a flash:
 * 1. Synchronously from the sessionStorage cache (instant, may be slightly
 *    stale if the user's level changed since login).
 * 2. Reconciled in the background from `GET /auth/session`, which returns the
 *    server's current permission set. Fire-and-forget — the UI is already
 *    usable from step 1, and the server enforces regardless.
 */
function restoreSession() {
    if (!isSessionValid())
        return;
    const token = getSessionToken();
    (0, state_1.updateState)({
        isAuthenticated: true,
        sessionToken: token,
        permissions: getStoredPermissions(),
        currentPage: "events",
    });
    console.log("[Auth] Session restored from storage");
    void reconcilePermissions();
}
/**
 * Refresh permissions from the server for an already-restored session.
 * Never throws (network failures leave the cached snapshot in place); an
 * invalidated session is left for the normal per-request 401 handling.
 */
async function reconcilePermissions() {
    const token = getSessionToken();
    if (!token)
        return;
    try {
        const response = await fetch(API_PROXY_URL + "/auth/session", {
            method: "GET",
            headers: { "X-Session-Token": token },
        });
        const data = await response.json();
        if (!data?.valid)
            return;
        const fresh = parsePermissions(data.permissions);
        storePermissions(fresh);
        if ((0, state_1.getState)().isAuthenticated) {
            (0, state_1.updateState)({ permissions: fresh });
        }
    }
    catch (error) {
        console.error("Failed to reconcile permissions:", error);
    }
}
/**
 * Logout user
 * OPTIMIZED: Clear memory cache
 * SECURITY: Invalidate session on server
 */
async function logout() {
    const sessionToken = getSessionToken();
    // Invalidate session on server
    if (sessionToken) {
        try {
            await fetch(API_PROXY_URL + "/auth/logout", {
                method: "POST",
                headers: {
                    "X-Session-Token": sessionToken,
                },
            });
            console.log("[Auth] Session invalidated on server");
        }
        catch (error) {
            console.error("Failed to logout on server:", error);
        }
    }
    // Clear local session
    clearSessionToken();
    storePermissions(null);
    // Clear session storage
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_EXPIRY_KEY);
    sessionStorage.removeItem(SECRET_KEY_KEY);
    (0, state_1.updateState)({
        isAuthenticated: false,
        sessionToken: null,
        permissions: null,
        currentPage: "login",
    });
    console.log("[Auth] Logged out successfully");
}
/**
 * Rate limiter for failed auth attempts
 */
const failedAttempts = new Map();
const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
const MAX_ATTEMPTS = 5;
/**
 * Check if user is rate limited
 */
function isRateLimited() {
    const now = Date.now();
    const key = "login_attempts";
    if (!failedAttempts.has(key)) {
        failedAttempts.set(key, []);
    }
    const attempts = failedAttempts.get(key);
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter((time) => now - time < RATE_LIMIT_WINDOW);
    failedAttempts.set(key, recentAttempts);
    return recentAttempts.length >= MAX_ATTEMPTS;
}
/**
 * Record failed auth attempt
 */
function recordFailedAttempt() {
    const key = "login_attempts";
    if (!failedAttempts.has(key)) {
        failedAttempts.set(key, []);
    }
    failedAttempts.get(key).push(Date.now());
}
// ============================================================================
// Kalshi API Credentials Management
// ============================================================================
/**
 * Store Kalshi API credentials
 */
function storeApiCredentials(credentials) {
    try {
        if (!credentials.accessKey) {
            throw new Error("Missing API credentials");
        }
        // Set in API module (memory only — never persisted, so an injected
        // script can't lift the secret from sessionStorage)
        (0, api_1.setApiCredentials)(credentials);
        console.log("API credentials stored successfully");
        return true;
    }
    catch (error) {
        console.error("Failed to store API credentials:", error);
        (0, state_1.updateState)({
            errorMessage: `Failed to store credentials: ${error instanceof Error ? error.message : "Unknown error"}`,
        });
        return false;
    }
}
/**
 * Check if API credentials are set (in memory).
 */
function hasApiCredentials() {
    const credentials = (0, api_1.getApiCredentials)();
    return !!credentials?.accessKey;
}
/**
 * Clear Kalshi API credentials (memory + any legacy persisted copy)
 */
function clearApiCredentialsStored() {
    try {
        (0, api_1.clearCredentials)();
        console.log("API credentials cleared");
    }
    catch (error) {
        console.error("Failed to clear API credentials:", error);
    }
}
/**
 * Validate API credentials by attempting a health check
 */
async function validateApiCredentials(credentials) {
    try {
        // This would require importing the Kalshi API module
        // For now, just validate the format
        if (!credentials.accessKey ||
            credentials.accessKey.trim().length === 0) {
            throw new Error("Invalid access key");
        }
        console.log("API credentials format is valid");
        return true;
    }
    catch (error) {
        console.error("API credentials validation failed:", error instanceof Error ? error.message : "Unknown error");
        return false;
    }
}
